import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { memo, useCallback, useMemo, useState, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { Badge } from '../Badge';
import { Icon, } from '../Icon/index';
const borderWidth = 1;
const focusedBorderColor = theme.colors.gray700;
const focusedBorderWidth = 2;
const iconButtonSizes = {
    medium: 40,
    small: 32,
};
const buttonWithLabelHeights = {
    medium: 82,
    small: 54,
};
const buttonWithLabelWidths = {
    medium: 82,
    small: 48,
};
const contentStyles = css `
  align-items: center;
  display: inline-flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
`;
const badgeStyles = css `
  display: inline-flex;
  position: absolute;
  right: 0;
`;
const badgeContainerStyles = css `
  position: absolute;
  width: 100%;
`;
const iconContainerStyles = css `
  ${styleUtils.borderBox}
  display: flex;
  flex-direction: row;
  cursor: pointer;
`;
const labelStyles = css `
  ${theme.typeStyles.bodyExtraSmallStyles}
  height: 100%;
  overflow: hidden;
  padding-top: ${theme.spacing.base};
  width: 100%;
`;
const defaultProps = {
    disabled: false,
    isOn: false,
    modifier: 'primary',
    size: 'medium',
};
const IconToggleButton = ({ badgeText, disabled, height, iconType, id, isOn, label, modifier = defaultProps.modifier, name, onClick, size = defaultProps.size, width, }) => {
    const [isFocused, setisFocused,] = useState(false);
    const [isHovering, setIsHovering,] = useState(false);
    const onFocus = useCallback(() => {
        setisFocused(true);
    }, []);
    const onBlur = useCallback(() => {
        setisFocused(false);
    }, []);
    const onPointerEnter = useCallback(() => {
        setIsHovering(true);
    }, []);
    const onPointerLeave = useCallback(() => {
        setIsHovering(false);
    }, []);
    const labelId = useMemo(() => (label
        ? `label-for-${id || name}`
        : undefined), [
        id,
        label,
        name,
    ]);
    const iconStyles = useMemo(() => (css `
        ${styleUtils.borderBox}
        align-items: center;
        background-color: ${theme.colors.gray100};
        border-color: transparent;
        border-radius: 50%;
        border-style: solid;
        border-width: ${styleUtils.rem(borderWidth)};
        color: ${theme.colors.gray700};
        display: inline-flex;
        height: ${styleUtils.rem(height || iconButtonSizes[size])};
        justify-content: center;
        overflow: hidden;
        padding: calc(${theme.spacing.base} - ${styleUtils.rem(borderWidth)});
        white-space: nowrap;
        width: ${styleUtils.rem(width || iconButtonSizes[size])};

        ${modifier === 'primary' && css `
          border-color: ${theme.colors.gray400};
        `}

        ${modifier === 'secondary' && css `
          background-color: transparent;
        `}

        ${isOn && css `
          background-color: ${theme.colors.accent700};
          color: ${theme.colors.gray100};
        `}

        ${isOn && (modifier === 'primary') && css `
          border-color: ${theme.colors.accent700};
        `}

        ${!disabled && (isHovering || isFocused) && css `
          outline: none;
          cursor: pointer;
          background-color: ${theme.colors.gray300};
          color: ${theme.colors.gray700};

          ${isOn && css `
            background-color: ${theme.colors.accent800};
            color: ${theme.colors.gray100};
          `}

          ${isOn && (modifier === 'primary') && css `
            border-color: ${theme.colors.accent800};
          `}

          ${!label && isFocused && css `
            outline-color: ${focusedBorderColor};
            outline-style: solid;
            outline-width: ${styleUtils.rem(focusedBorderWidth)};
          `}
        `}

        ${disabled && css `
          color: ${theme.colors.gray700};
          cursor: default;
          opacity: 0.2;
          outline: none;

          ${modifier === 'primary' && css `
            background-color: ${theme.colors.gray100};
            border-color: ${theme.colors.gray700};
          `}

          ${modifier === 'secondary' && css `
            background-color: transparent;
          `}
        `}
      `), [
        isOn,
        disabled,
        height,
        isFocused,
        isHovering,
        label,
        modifier,
        size,
        width,
    ]);
    const buttonWithLabelStyles = useMemo(() => (css `
      ${styleUtils.buttonReset}
        align-items: center;
        display: inline-flex;
        flex-direction: column;
        height: ${styleUtils.rem(buttonWithLabelHeights[size])};
        width: ${styleUtils.rem(buttonWithLabelWidths[size])};

        ${!disabled && css `
          cursor: pointer;

          :focus {
            border-radius: ${styleUtils.rem(2)};
            outline-color: ${focusedBorderColor};
            outline-style: solid;
            outline-width: ${styleUtils.rem(focusedBorderWidth)};
            outline-offset: ${focusedBorderWidth};
          }
        `}
        &:focus-visible {
          ${styleUtils.focusVisibleStyles};
        }
      `), [
        disabled,
        size,
    ]);
    const buttonStyles = useMemo(() => (label
        ? buttonWithLabelStyles
        : iconStyles), [
        label,
        buttonWithLabelStyles,
        iconStyles,
    ]);
    const iconComponent = useMemo(() => {
        const icon = (_jsx(Icon, { size: size, type: iconType }));
        return (label
            ? (_jsx("span", Object.assign({ css: iconContainerStyles }, { children: _jsx("span", Object.assign({ css: iconStyles }, { children: icon })) })))
            : icon);
    }, [
        label,
        iconStyles,
        iconType,
        size,
    ]);
    return (_jsx("button", Object.assign({ "aria-label": label, "aria-labelledby": labelId, "aria-pressed": isOn, css: buttonStyles, disabled: disabled, id: id || name, name: name, onBlur: onBlur, onClick: onClick, onFocus: onFocus, onPointerEnter: onPointerEnter, onPointerLeave: onPointerLeave, type: "button" }, { children: _jsxs("span", Object.assign({ css: contentStyles }, { children: [iconComponent, label
                    && (_jsx("span", Object.assign({ css: labelStyles, id: labelId }, { children: label }))), label
                    && badgeText
                    && (_jsx("div", Object.assign({ css: badgeContainerStyles }, { children: _jsx("span", Object.assign({ css: badgeStyles }, { children: _jsx(Badge, Object.assign({ size: "small", type: 'positiveLight' }, { children: badgeText })) })) })))] })) })));
};
const MemoizedIconToggleButton = memo(IconToggleButton);
export { MemoizedIconToggleButton as IconToggleButton };
// MC-1068 Temporary hack for Storybook.
export default IconToggleButton;
